import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Modal, Field, Notice, RowCard } from '../patterns';

interface TenantOpt { _id: string; tenantId: string; name: string; }
interface UnlinkedPest { _id: string; code: string; name: string; }

interface Props {
  show: boolean;
  tenants: TenantOpt[];
  onHide: () => void;
  onPromoted: () => void;
}

const PromoteFromTenantModal: React.FC<Props> = ({ show, tenants, onHide, onPromoted }) => {
  const { t } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;

  const [tenantId, setTenantId] = useState('');
  const [pests, setPests] = useState<UnlinkedPest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchUnlinked = async (tid: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get<UnlinkedPest[]>(
        `/api/v1/global-pests/tenant-pests/${encodeURIComponent(tid)}`,
        { params: { unlinkedOnly: 'true' }, withCredentials: true }
      );
      setPests(res.data);
    } catch {
      setError(tRef.current('system.pests.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (tenantId) {
      fetchUnlinked(tenantId);
    } else {
      setPests([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const handlePromote = async (pestId: string) => {
    try {
      await axios.post('/api/v1/global-pests/promote', { tenantId, pestId }, { withCredentials: true });
      // Refresh the unlinked list (the promoted pest disappears from it)
      await fetchUnlinked(tenantId);
      onPromoted();
    } catch {
      setError(tRef.current('system.pests.errors.promoteFailed'));
    }
  };

  return (
    <Modal show={show} onHide={onHide} title={t('system.pests.promote.title')} size="md">
      <Modal.Body>
        <Field
          label={t('system.pests.promote.tenantLabel')}
          controlId="promote-tenant"
          required
        >
          <select
            id="promote-tenant"
            className="pat-input"
            value={tenantId}
            onChange={e => setTenantId(e.target.value)}
          >
            <option value="">—</option>
            {tenants
              .filter(tn => tn.tenantId !== 'system')
              .map(tn => (
                <option key={tn._id} value={tn.tenantId}>
                  {tn.name} ({tn.tenantId})
                </option>
              ))}
          </select>
        </Field>

        {tenantId && (
          <p className="pat-field__help" style={{ marginTop: 8 }}>
            {t('system.pests.promote.subtitle')}
          </p>
        )}

        {tenantId && !loading && pests.length === 0 && (
          <p className="pat-field__help" style={{ marginTop: 8 }}>
            {t('system.pests.promote.empty')}
          </p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
          {pests.map(p => (
            <div key={p._id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <RowCard icon="bi-bug" title={p.name} subtitle={p.code} />
              </div>
              <button
                type="button"
                className="pat-btn pat-btn--primary"
                onClick={() => handlePromote(p._id)}
              >
                {t('system.pests.promote.promote')}
              </button>
            </div>
          ))}
        </div>

        {error && (
          <Notice tone="danger">
            {error}
          </Notice>
        )}
      </Modal.Body>
      <Modal.Footer>
        <button type="button" className="pat-btn pat-btn--subtle" onClick={onHide}>
          {t('system.pests.promote.close')}
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default PromoteFromTenantModal;
